import { Validators } from '@angular/forms';
import {
  CancerHistoryStatus,
  InVitroTypes,
  PregnancyType,
  ValidationPair,
} from '@app/modules/service-data/types/service-data.types';
import { gestationAgeValidValidator } from '@app/modules/service-data/validators/gestation-age-valid.validator';
import { crossFieldValidator } from '@shared/validators/cross-field-validator';
import { diseasesValidator } from '@shared/validators/diseases-validator';
import { errorValueValidator } from '@shared/validators/error-value-validator';

export const monoValidationSet: ValidationPair = {
  children: {
    mono: {
      children: {
        monogenicIncidentalFindings: {
          validators: [Validators.required],
        },
        patientHistory: {
          children: {
            monogenicDisorderCarrier: {
              children: {
                isCarrier: {
                  validators: [Validators.required, errorValueValidator(true)],
                },
              },
            },
          },
        },
      },
    },
    shared: {
      children: {
        doctorInformation: {
          children: {
            doctorId: {
              validators: [Validators.required],
            },
          },
        },
        patientInformation: {
          children: {
            height: {
              validators: [Validators.required, Validators.min(0)],
            },
            weight: {
              validators: [Validators.required, Validators.min(0)],
            },
          },
        },
        patientHistory: {
          children: {
            cancerHistory: {
              children: {
                hasHistory: {
                  validators: [Validators.required],
                },
                status: {
                  validators: [
                    errorValueValidator(CancerHistoryStatus.CurrentlyAffected),
                  ],
                },
              },
            },
            albuminTherapy: {
              children: {
                hasReceived: {
                  validators: [Validators.required],
                },
                isRelevant: {
                  validators: [errorValueValidator(true)],
                },
              },
            },
            cellularImmunotherapy: {
              children: {
                hasReceived: {
                  validators: [Validators.required],
                },
                isRelevant: {
                  validators: [errorValueValidator(true)],
                },
              },
            },
            stemCellTherapy: {
              children: {
                hasReceived: {
                  validators: [Validators.required, errorValueValidator(true)],
                },
              },
            },
            transplantSurgery: {
              children: {
                hasReceived: {
                  validators: [Validators.required, errorValueValidator(true)],
                },
              },
            },
            bloodTransfusion: {
              children: {
                hasReceived: {
                  validators: [Validators.required],
                },
                isRelevant: {
                  validators: [errorValueValidator(true)],
                },
              },
            },
            familyDiseaseHistory: {
              children: {
                hasHistory: {
                  validators: [Validators.required],
                },
              },
              validators: [diseasesValidator(true)],
            },
            pregnancyDiseaseHistory: {
              children: {
                hasHistory: {
                  validators: [Validators.required],
                },
              },
              validators: [diseasesValidator()],
            },
            carrierOfGeneticDiseases: {
              validators: [diseasesValidator()],
            },
          },
        },
        currentPregnancyInformation: {
          children: {
            expectedConfinementDate: {
              validators: [Validators.required],
            },
            gestationAge: {
              children: {
                week: {
                  validators: [
                    Validators.required,
                    Validators.min(10),
                    Validators.max(24),
                  ],
                },
                day: {
                  validators: [
                    Validators.required,
                    Validators.min(0),
                    Validators.max(6),
                  ],
                },
              },
            },
            isGestationAgeAutomaticallyCalculated: {
              validators: [Validators.required],
            },
            pregnancyType: {
              validators: [
                Validators.required,
                errorValueValidator(PregnancyType.twins),
              ],
            },
            inVitro: {
              children: {
                isInVitroFertilized: {
                  validators: [Validators.required],
                },
                vitroType: {
                  validators: [errorValueValidator(InVitroTypes.DonorEgg)],
                },
                isSurrogateMother: {
                  validators: [errorValueValidator(true)],
                },
              },
            },
            vanishingTwinSyndrome: {
              children: {
                isDetected: {
                  validators: [Validators.required],
                },
                detectedGestationAge: {
                  children: {
                    week: {
                      validators: [Validators.min(1), Validators.max(7)],
                    },
                    day: {
                      validators: [Validators.min(0), Validators.max(6)],
                    },
                  },
                },
              },
            },
            heparinTherapy: {
              children: {
                hasReceived: {
                  validators: [Validators.required],
                },
                isRelevant: {
                  validators: [errorValueValidator(true)],
                },
              },
            },
          },
        },
      },
      validators: [
        gestationAgeValidValidator(),
        crossFieldValidator(
          'patientHistory.cancerHistory.status',
          'patientHistory.cancerHistory.hasHistory',
          true
        ),
        crossFieldValidator(
          'patientHistory.albuminTherapy.isRelevant',
          'patientHistory.albuminTherapy.hasReceived',
          true
        ),
        crossFieldValidator(
          'patientHistory.cellularImmunotherapy.isRelevant',
          'patientHistory.cellularImmunotherapy.hasReceived',
          true
        ),
        crossFieldValidator(
          'patientHistory.bloodTransfusion.isRelevant',
          'patientHistory.bloodTransfusion.hasReceived',
          true
        ),
        crossFieldValidator(
          'currentPregnancyInformation.inVitro.isSurrogateMother',
          'currentPregnancyInformation.inVitro.isInVitroFertilized',
          true
        ),
        crossFieldValidator(
          'currentPregnancyInformation.inVitro.vitroType',
          'currentPregnancyInformation.inVitro.isInVitroFertilized',
          true
        ),
        crossFieldValidator(
          'currentPregnancyInformation.vanishingTwinSyndrome.detectedGestationAge.week',
          'currentPregnancyInformation.vanishingTwinSyndrome.isDetected',
          true
        ),
        crossFieldValidator(
          'currentPregnancyInformation.vanishingTwinSyndrome.detectedGestationAge.day',
          'currentPregnancyInformation.vanishingTwinSyndrome.isDetected',
          true
        ),
        crossFieldValidator(
          'currentPregnancyInformation.heparinTherapy.isRelevant',
          'currentPregnancyInformation.heparinTherapy.hasReceived',
          true
        ),
      ],
    },
  },
};
